// Plan 23b Task 4.1 — Emote Wheel primitive.
// Behavior contract: spec §10 lines 755–790.
//
// Radial 6-sector wheel driven by a `getAim()` callback (gamepad right-stick
// or any Vec2 source). Keyboard shortcuts 1-6 fire emote directly without
// showing the wheel. Built on GsapRegistry (F20a-CLASS-1 sentinel-safe
// timeline positions — every tl.to/fromTo/call uses explicit numeric position).

import type { UIComponentHandle, UIComponentConfig } from './types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';
import type { EmoteId } from '../../emotes/types';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface EmoteWheelOpts {
  /** Returns current aim direction, or null when stick is released. */
  getAim?: () => { x: number; y: number } | null;
  /** Event target for keyboard shortcut (Digit1–Digit6). Default: window. */
  keydownTarget?: EventTarget;
  /** Stick deadzone — aim magnitude below this is treated as null. Default: 0.15. */
  deadzone?: number;
  /** DOM icon URLs keyed by emote id. */
  iconUrls?: Partial<Record<EmoteId, string>>;
  /** Legacy Digit1-Digit6 shortcuts. Runtime-owned wheels pass false. */
  keyboardShortcuts?: boolean;
}

export type EmoteWheelHandle = UIComponentHandle & {
  onFire(cb: (emoteId: 1 | 2 | 3 | 4 | 5 | 6) => void): void;
  tick(deltaSec: number): void;
  show(selectedEmoteId?: EmoteId | null): void;
  hide(): void;
  setSelectedEmoteId(emoteId: EmoteId | null): void;
  setIconUrls(iconUrls: Partial<Record<EmoteId, string>>): void;
};

// ─────────────────────────────────────────────────────────────────────────────
// Sector definitions (fixed, from spec)
// ─────────────────────────────────────────────────────────────────────────────

const EMOTE_SECTORS = [
  { id: 1, name: 'GG', angleDeg: 0 },
  { id: 2, name: 'Oof', angleDeg: 60 },
  { id: 3, name: 'Taunt', angleDeg: 120 },
  { id: 4, name: 'Thanks', angleDeg: 180 },
  { id: 5, name: 'Cry', angleDeg: 240 },
  { id: 6, name: 'Cheer', angleDeg: 300 },
] as const;

type SectorId = (typeof EMOTE_SECTORS)[number]['id'];

// ─────────────────────────────────────────────────────────────────────────────
// Design token constants — from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** color.ui.panel-bg-alpha — sector background */
const SECTOR_BG = 'rgba(30, 35, 50, 0.82)';

/** color.ui.overlay-dim-light — wheel backdrop */
const BACKDROP_BG = 'rgba(0, 0, 0, 0.35)';

/** color.brand.amber — highlight color */
const AMBER = '#E8A020';

/** border.focus-gamepad — "3px solid #E8A020" — highlighted sector border */
const HIGHLIGHT_BORDER = '3px solid #E8A020';

/** shadow.glow-amber — "0 0 12px rgba(232,160,32,0.50)" — highlight glow */
const GLOW_AMBER = '0 0 12px rgba(232,160,32,0.50)';

/** border-radius.circle — 50% for sector pill shape */
const BORDER_RADIUS_CIRCLE = '50%';

/** Above board HUD/cinematic controls, below pause/system overlays. */
const Z_INDEX_OVERLAY = 560;

/** animation.duration.fast = 150ms → 0.15s for GSAP */
const FAST_S = 0.15;

const WHEEL_SIZE_CSS = 'min(560px, calc(100vw - 32px), calc(100vh - 32px))';
const SECTOR_SIZE_CSS = 'clamp(104px, 26vw, 148px)';
const ICON_SIZE_CSS = 'clamp(74px, 18vw, 108px)';
const SECTOR_RADIUS_PERCENT = 32;

// ─────────────────────────────────────────────────────────────────────────────
// Angle utilities
// ─────────────────────────────────────────────────────────────────────────────

/** Normalise an angle to [0, 360). */
function normAngle(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/** Angular distance between two angles in [0, 180]. */
function angularDist(a: number, b: number): number {
  const diff = Math.abs(normAngle(a) - normAngle(b));
  return diff > 180 ? 360 - diff : diff;
}

/** Given aim {x,y}, return the closest sector id. */
function closestSector(x: number, y: number): SectorId {
  // atan2(-y, x) because CSS y-axis is flipped relative to maths
  const aimDeg = normAngle((Math.atan2(-y, x) * 180) / Math.PI);
  let best: SectorId = 1;
  let bestDist = Infinity;
  for (const s of EMOTE_SECTORS) {
    const d = angularDist(aimDeg, s.angleDeg);
    if (d < bestDist) {
      bestDist = d;
      best = s.id;
    }
  }
  return best;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createEmoteWheel(config: UIComponentConfig & EmoteWheelOpts): EmoteWheelHandle {
  const {
    gsapRegistry,
    mount,
    getAim = () => null,
    keydownTarget = window,
    deadzone = 0.15,
    iconUrls = {},
    keyboardShortcuts = true,
  } = config;

  let fireCb: ((emoteId: SectorId) => void) | undefined;
  let disposed = false;

  // Track visibility state
  let isVisible = false;
  let currentSectorId: SectorId = 1;
  let animHandle: GsapTimelineHandle | null = null;

  // ── Build DOM ────────────────────────────────────────────────────────────

  // Root wheel element: full-screen overlay with a large centered wheel.
  const wheelEl = document.createElement('div');
  wheelEl.className = 'ui-emote-wheel';
  wheelEl.style.cssText = [
    'position: fixed',
    'inset: 0',
    'padding: max(8px, env(safe-area-inset-top)) max(8px, env(safe-area-inset-right)) max(8px, env(safe-area-inset-bottom)) max(8px, env(safe-area-inset-left))',
    'box-sizing: border-box',
    `z-index: ${Z_INDEX_OVERLAY}`,
    `background-color: ${BACKDROP_BG}`,
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'pointer-events: auto',
    // Start hidden (opacity: 0, removed from DOM)
  ].join('; ');

  const wheelFrame = document.createElement('div');
  wheelFrame.className = 'ui-emote-wheel__frame';
  wheelFrame.style.cssText = [
    'position: relative',
    `width: ${WHEEL_SIZE_CSS}`,
    'aspect-ratio: 1 / 1',
    'border-radius: 50%',
    'pointer-events: none',
  ].join('; ');
  wheelEl.appendChild(wheelFrame);

  const centerHub = document.createElement('div');
  centerHub.className = 'ui-emote-wheel__hub';
  centerHub.style.cssText = [
    'position: absolute',
    'left: 50%',
    'top: 50%',
    'width: clamp(72px, 16vw, 116px)',
    'height: clamp(72px, 16vw, 116px)',
    'transform: translate(-50%, -50%)',
    'border-radius: 50%',
    'background: rgba(12, 14, 20, 0.72)',
    'border: 3px solid rgba(255,255,255,0.18)',
    'box-shadow: inset 0 2px 0 rgba(255,255,255,0.15), 0 8px 22px rgba(0,0,0,0.36)',
    'pointer-events: none',
  ].join('; ');
  wheelFrame.appendChild(centerHub);

  // Build sector elements
  const sectorEls = new Map<SectorId, HTMLDivElement>();

  for (const sector of EMOTE_SECTORS) {
    const rad = (sector.angleDeg * Math.PI) / 180;
    const left = 50 + SECTOR_RADIUS_PERCENT * Math.cos(rad);
    const top = 50 - SECTOR_RADIUS_PERCENT * Math.sin(rad);

    const sectorEl = document.createElement('div');
    sectorEl.className = 'ui-emote-wheel__sector';
    sectorEl.setAttribute('data-emote-id', String(sector.id));
    sectorEl.setAttribute('role', 'button');
    sectorEl.setAttribute('aria-label', sector.name);
    sectorEl.tabIndex = -1;
    sectorEl.style.cssText = [
      'position: absolute',
      `width: ${SECTOR_SIZE_CSS}`,
      `height: ${SECTOR_SIZE_CSS}`,
      `left: ${left}%`,
      `top: ${top}%`,
      'transform: translate(-50%, -50%)',
      `background-color: ${SECTOR_BG}`,
      `border-radius: ${BORDER_RADIUS_CIRCLE}`,
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'justify-content: center',
      'gap: 4px',
      'border: 3px solid transparent',
      'box-shadow: none',
      'box-sizing: border-box',
      'cursor: pointer',
      'pointer-events: auto',
      'touch-action: none',
    ].join('; ');
    sectorEl.addEventListener('pointerenter', () => setHighlight(sector.id));
    sectorEl.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setHighlight(sector.id);
      fireCb?.(sector.id);
    });

    // Label span
    const label = document.createElement('span');
    label.className = 'ui-emote-wheel__sector-label';
    label.textContent = sector.name;
    label.style.cssText = [
      "font-family: 'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif",
      'font-size: clamp(16px, 3.2vw, 20px)',
      'font-weight: 900',
      'line-height: 1',
      'color: #FFFFFF',
      'text-shadow: 0 2px 0 #000000, 0 4px 8px rgba(0,0,0,0.40)',
      'user-select: none',
      'pointer-events: none',
    ].join('; ');

    sectorEl.appendChild(label);
    wheelFrame.appendChild(sectorEl);
    sectorEls.set(sector.id as SectorId, sectorEl);
  }

  function setSectorIcon(id: SectorId, url: string | undefined): void {
    const sectorEl = sectorEls.get(id);
    if (sectorEl == null) return;
    const label = sectorEl.querySelector('.ui-emote-wheel__sector-label');
    let image = sectorEl.querySelector<HTMLImageElement>('.ui-emote-wheel__sector-icon');
    if (url == null || url === '') {
      image?.remove();
      if (label instanceof HTMLElement) label.style.display = '';
      return;
    }
    if (image == null) {
      image = document.createElement('img');
      image.className = 'ui-emote-wheel__sector-icon';
      image.alt = '';
      image.draggable = false;
      image.style.cssText = [
        `width: ${ICON_SIZE_CSS}`,
        `height: ${ICON_SIZE_CSS}`,
        'border-radius: 50%',
        'object-fit: cover',
        'display: block',
        'pointer-events: none',
      ].join('; ');
      sectorEl.appendChild(image);
    }
    image.src = url;
    if (label instanceof HTMLElement) label.style.display = 'none';
  }

  function setIconUrls(nextIconUrls: Partial<Record<EmoteId, string>>): void {
    for (const sector of EMOTE_SECTORS) {
      setSectorIcon(sector.id, nextIconUrls[sector.id]);
    }
  }

  setIconUrls(iconUrls);

  // ── Highlight helpers ────────────────────────────────────────────────────

  function setHighlight(id: SectorId): void {
    for (const [sid, el] of sectorEls) {
      if (sid === id) {
        el.classList.add('ui-emote-wheel__sector--highlighted');
        el.style.border = HIGHLIGHT_BORDER;
        el.style.boxShadow = GLOW_AMBER;
        el.style.backgroundColor = SECTOR_BG;
        // Amber tint on highlighted sector
        el.style.outline = `2px solid ${AMBER}`;
      } else {
        el.classList.remove('ui-emote-wheel__sector--highlighted');
        el.style.border = '3px solid transparent';
        el.style.boxShadow = 'none';
        el.style.outline = 'none';
      }
    }
    currentSectorId = id;
  }

  // ── Kill in-flight animation ─────────────────────────────────────────────

  function killAnim(): void {
    if (animHandle) {
      animHandle.dispose();
      animHandle = null;
    }
  }

  // ── Mount / unmount wheel ────────────────────────────────────────────────

  function mountWheel(): void {
    if (!wheelEl.parentNode) {
      const parent = mount ?? document.body;
      parent.appendChild(wheelEl);
    }
  }

  function removeWheel(): void {
    if (wheelEl.parentNode) {
      wheelEl.parentNode.removeChild(wheelEl);
    }
  }

  // ── Show animation ───────────────────────────────────────────────────────

  function showWheel(): void {
    killAnim();
    mountWheel();
    const handle = gsapRegistry.create('emote-wheel:open');
    const tl = handle.timeline;
    // fromTo with explicit numeric position = 0 (F20a-CLASS-1 sentinel-safe)
    tl.fromTo(
      wheelEl,
      { scale: 0.6, opacity: 0 },
      { scale: 1, opacity: 1, duration: FAST_S, ease: 'power2.out' },
      0,
    );
    animHandle = handle;
    isVisible = true;
  }

  // ── Hide animation ───────────────────────────────────────────────────────

  function hideWheel(): void {
    if (!wheelEl.parentNode && !isVisible) return;
    killAnim();
    const handle = gsapRegistry.create('emote-wheel:close');
    const tl = handle.timeline;
    // Close tween at position 0 (F20a-CLASS-1)
    tl.to(wheelEl, { scale: 0.6, opacity: 0, duration: FAST_S, ease: 'power2.in' }, 0);
    // Detach from DOM after fade-out at absolute FAST_S (F20a-CLASS-1)
    tl.call(removeWheel, undefined, FAST_S);
    animHandle = handle;
    isVisible = false;
  }

  function setSelectedEmoteId(emoteId: EmoteId | null): void {
    if (emoteId == null) {
      for (const [, el] of sectorEls) {
        el.classList.remove('ui-emote-wheel__sector--highlighted');
        el.style.border = '3px solid transparent';
        el.style.boxShadow = 'none';
        el.style.outline = 'none';
      }
      return;
    }
    setHighlight(emoteId);
  }

  function show(selectedEmoteId: EmoteId | null = currentSectorId): void {
    showWheel();
    setSelectedEmoteId(selectedEmoteId);
  }

  function hide(): void {
    hideWheel();
  }

  // ── tick() ───────────────────────────────────────────────────────────────

  function tick(_deltaSec: number): void {
    if (disposed) return;

    const aim = getAim();
    const magnitude = aim ? Math.sqrt(aim.x * aim.x + aim.y * aim.y) : 0;

    if (!aim || magnitude < deadzone) {
      // Stick released (or below deadzone)
      if (isVisible) {
        // Fire the current sector and hide
        fireCb?.(currentSectorId);
        hideWheel();
      }
      // else: was already hidden, no-op
    } else {
      // Stick active
      if (!isVisible) {
        showWheel();
      }
      // Update highlight
      const sectorId = closestSector(aim.x, aim.y);
      setHighlight(sectorId);
    }
  }

  // ── Keyboard handler ─────────────────────────────────────────────────────

  const onKeydown = (e: Event): void => {
    if (disposed) return;
    const ke = e as KeyboardEvent;
    const digit = parseInt(ke.code.replace('Digit', ''), 10);
    if (digit >= 1 && digit <= 6) {
      fireCb?.(digit as SectorId);
    }
  };

  if (keyboardShortcuts) {
    keydownTarget.addEventListener('keydown', onKeydown);
  }

  // ── dispose() ────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Remove keyboard listener
    if (keyboardShortcuts) {
      keydownTarget.removeEventListener('keydown', onKeydown);
    }

    // Kill any in-flight animation
    killAnim();

    // Detach from DOM
    removeWheel();
  }

  // ── Optional mount at construction time ──────────────────────────────────
  // NOTE: wheel is intentionally NOT mounted at construction — it starts hidden.
  // The `mount` parameter is stored and used when the wheel is first shown.

  return {
    element: wheelEl,
    onFire(cb: (emoteId: SectorId) => void): void {
      fireCb = cb;
    },
    tick,
    show,
    hide,
    setSelectedEmoteId,
    setIconUrls,
    dispose,
  };
}
